import { ScoreRing } from "@/components/ScoreRing";
import { SkillTag } from "@/components/SkillTag";
import { MapPin, Briefcase, Globe, BadgeCheck, CircleDot, Check, Lightbulb, Info } from "lucide-react";
import { Silhouette } from "@/components/Silhouette";
import { InfoTip } from "@/components/InfoTip";

export function PassportView({ p, showInsights = false, hideAbout = false }) {
  return (
    <div className="space-y-5">
      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6 flex flex-col items-center text-center">
        {p.photo_url ? (
          <img src={p.photo_url} alt={p.name} className="h-24 w-24 rounded-full object-cover border-4 border-white shadow-md" data-testid="avatar-photo" />
        ) : (
          <div className="h-24 w-24 rounded-full bg-slate-100 border-4 border-white shadow-md flex items-center justify-center text-slate-300" data-testid="avatar-placeholder" title="Bez fotografie">
            <Silhouette gender={p.gender} size={52} />
          </div>
        )}
        <h2 className="mt-3 font-head text-2xl font-bold tracking-tight">{p.name}</h2>
        <p className="text-slate-600">{p.title}</p>
        {p.verified && (
          <span className="mt-2 inline-flex items-center gap-1 text-emerald-600 text-sm font-semibold"><BadgeCheck size={16} /> Overený profil</span>
        )}
        <div className="mt-4"><ScoreRing value={p.career_score || 0} size={110} /></div>
        <div className="mt-4 flex flex-wrap justify-center gap-2 text-sm text-slate-600">
          {p.location && <Meta icon={MapPin} text={p.location} />}
          {p.years_experience != null && <Meta icon={Briefcase} text={`${p.years_experience} rokov praxe`} />}
        </div>
        {p.availability && (
          <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-sm font-medium text-emerald-700">
            <CircleDot size={14} /> {p.availability}
          </span>
        )}
      </div>

      {p.about && !hideAbout && (
        <Section title="O mne"><p className="text-slate-600 leading-relaxed">{p.about}</p></Section>
      )}

      {showInsights && p.career_assessment?.breakdown ? (
        <Section title="Career Score">
          <div className="flex items-end gap-3 mb-4" data-testid="career-score-card">
            <span className="font-head text-4xl font-bold text-blue-700" data-testid="career-score-value">{p.career_assessment.score}</span>
            <span className="text-slate-400 text-lg mb-1">/ 100</span>
            <span className="ml-auto text-sm font-semibold text-slate-600" data-testid="career-score-label">{p.career_assessment.label}</span>
          </div>
          <div className="space-y-3" data-testid="career-breakdown">
            {Object.entries(p.career_assessment.breakdown).map(([k, v]) => (
              <div key={k} data-testid={`career-cat-${k}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-700 font-medium">{v.label} <span className="text-slate-400 text-xs">· {v.weight}%</span></span>
                  <span className="text-slate-500">{v.score}/100</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full rounded-full ${v.score >= 70 ? "bg-emerald-500" : v.score >= 45 ? "bg-blue-600" : "bg-amber-500"}`} style={{ width: `${v.score}%` }} />
                </div>
                {v.evidence && <p className="mt-1 text-xs text-slate-400">{v.evidence}</p>}
              </div>
            ))}
          </div>
          {p.career_assessment.not_specified?.length > 0 && (
            <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-3" data-testid="career-not-specified">
              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-500 uppercase tracking-wide"><Info size={13} /> Neuvedené v CV <InfoTip text="Toto nie je nedostatok – iba informácia, ktorá v CV chýba. Ak ju máš, doplnenie posilní profil." /></div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {p.career_assessment.not_specified.map((s, i) => (
                  <span key={i} className="rounded-full border border-dashed border-slate-300 bg-white px-2.5 py-0.5 text-xs font-medium text-slate-500">{s}</span>
                ))}
              </div>
            </div>
          )}
        </Section>
      ) : p.score_breakdown && Object.keys(p.score_breakdown).length > 0 && (
        <Section title="Career Score">
          <div className="space-y-3">
            {Object.entries(p.score_breakdown).map(([k, v]) => (
              <div key={k}>
                <div className="flex justify-between text-sm mb-1"><span className="text-slate-700 font-medium">{k}</span><span className="text-slate-500">{v}%</span></div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full bg-blue-600" style={{ width: `${v}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {showInsights && p.strengths?.length > 0 && (
        <Section title="Silné stránky">
          <ul className="space-y-2.5" data-testid="profile-strengths">
            {p.strengths.map((s, i) => (
              <li key={i} data-testid={`strength-${i}`} className="flex items-start gap-2.5 rounded-2xl bg-emerald-50 border border-emerald-100 px-4 py-3">
                <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white"><Check size={13} /></span>
                <span className="text-sm text-emerald-900 font-medium">{s}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {showInsights && p.improvements?.length > 0 && (
        <Section title="Oblasti na zlepšenie" tip="Konkrétne tipy, ako posilniť svoj profil na základe tvojho CV.">
          <ul className="space-y-2.5" data-testid="profile-improvements">
            {p.improvements.map((s, i) => (
              <li key={i} data-testid={`improvement-${i}`} className="flex items-start gap-2.5 rounded-2xl bg-amber-50 border border-amber-100 px-4 py-3">
                <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-amber-500 text-white"><Lightbulb size={13} /></span>
                <span className="text-sm text-amber-900 font-medium">{s}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {p.skills?.length > 0 && (
        <Section title="Schopnosti">
          <div className="flex flex-wrap gap-2">{p.skills.map((s, i) => <SkillTag key={i} name={s.name} stars={s.stars} />)}</div>
        </Section>
      )}

      {p.experiences?.length > 0 && (
        <Section title="Pracovné skúsenosti">
          <div className="space-y-5">
            {p.experiences.map((e, i) => (
              <div key={i} className="relative pl-4 border-l-2 border-blue-100">
                <div className="font-head font-semibold text-slate-900">{e.title}</div>
                <div className="text-sm text-slate-500">{[e.company, e.period].filter(Boolean).join(" · ")}</div>
                {e.bullets?.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {e.bullets.map((b, j) => <li key={j} className="text-sm text-slate-600 flex gap-2"><span className="text-blue-500">•</span>{b}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {p.career_dna?.length > 0 && (
        <Section title="Career DNA">
          <div className="flex flex-wrap gap-2">
            {p.career_dna.map((d, i) => (
              <span key={i} className="rounded-full bg-blue-50 border border-blue-100 px-3 py-1.5 text-sm font-medium text-blue-700">{d}</span>
            ))}
          </div>
        </Section>
      )}

      {p.education?.length > 0 && (
        <Section title="Vzdelanie">
          <div className="space-y-3">
            {p.education.map((e, i) => (
              <div key={i}>
                <div className="font-medium text-slate-800">{e.school}</div>
                <div className="text-sm text-slate-500">{[e.field, e.period].filter(Boolean).join(" · ")}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {p.languages?.length > 0 && (
        <Section title="Jazyky">
          <div className="space-y-2">
            {p.languages.map((l, i) => (
              <div key={i} className="flex justify-between items-center">
                <span className="flex items-center gap-2 text-slate-700"><Globe size={15} className="text-slate-400" />{l.name}</span>
                <span className="text-sm font-semibold text-slate-500">{l.level}</span>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function Meta({ icon: Icon, text }) {
  return <span className="inline-flex items-center gap-1"><Icon size={14} className="text-slate-400" />{text}</span>;
}

export function Section({ title, tip, children }) {
  return (
    <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6">
      <h3 className="font-head font-semibold text-lg mb-4 flex items-center gap-2">{title}{tip && <InfoTip text={tip} />}</h3>
      {children}
    </div>
  );
}
